import { Rect, Anchor, anchored, Point, move, Offset } from './geometry'

export type ViewportGeometry = {
  viewportRect: Rect
  scrollAreaRect: Rect
  childRect: Rect
}

type GetScrollTransform = {
  geom: ViewportGeometry
  viewportAnchor: Anchor
  childAnchor: Anchor
}

/**
 * When you have a viewport (for example a PDF viewer) within a scrollable area
 * and you want to bring a child element into view, there's a certain transform (dx, dy)
 * that needs to happen.
 *
 * For example, you might want to center element of the PDF within the viewport.
 *
 * Given the rectangles of the viewport, the entire scroll area, and the element,
 * we can calculate the translation necessary to scroll there.
 *
 * In the browser, we deal with scrollTop and scrollLeft, so {@link getTargetScrollPosition}
 * may a more convenient way to access this.
 *
 * @returns
 *  An offset (dx, dy) of how much we need to translate the viewport by in order to achieve
 *  the desired anchoring.
 */
export function getViewportTransform({
  geom,
  viewportAnchor,
  childAnchor
}: GetScrollTransform): Offset {
  // The desired viewport to achieve the anchoring necessary
  // for example we may want the center the child to be in the center
  // of the viewport
  const targetViewportRect = anchored({
    follower: geom.viewportRect,
    followerAnchor: viewportAnchor,
    target: geom.childRect,
    targetAnchor: childAnchor
  })

  // Based on the target viewport and the current viewport,
  // we can easily calculate the translatio required to go from the current
  // viewport to the desired viewport
  return {
    dx: targetViewportRect.x - geom.viewportRect.x,
    dy: targetViewportRect.y - geom.viewportRect.y
  }
}

type GetScrollPosition = {
  geom: ViewportGeometry
  viewportAnchor: Anchor
  childAnchor: Anchor
}
export function getTargetScrollPosition({
  geom,
  viewportAnchor,
  childAnchor
}: GetScrollPosition): Point {
  const scrollPosition: Point = {
    x: geom.viewportRect.x - geom.scrollAreaRect.x,
    y: geom.viewportRect.y - geom.scrollAreaRect.y
  }

  return move(scrollPosition, getViewportTransform({ geom, viewportAnchor, childAnchor }))
}

type ResolveScrollPositionArgs = {
  scrollArea: Rect
  viewport: Rect
  target: Rect
  viewportAlignment: Anchor
  targetAlignment: Anchor
}

export function resolveScrollPosition({
  scrollArea,
  viewport,
  target,
  viewportAlignment,
  targetAlignment
}: ResolveScrollPositionArgs): Point {
  const idealViewport = anchored({
    follower: viewport,
    followerAnchor: viewportAlignment,
    target,
    targetAnchor: targetAlignment
  })
  const clampedViewport = clampViewportToScrollArea(idealViewport, scrollArea)

  const currentScrollPosition = {
    x: viewport.x - scrollArea.x,
    y: viewport.y - scrollArea.y
  }
  const clampedScrollPosition = {
    x: clampedViewport.x - scrollArea.x,
    y: clampedViewport.y - scrollArea.y
  }

  return {
    x: isVisibleOnAxis(target.x, target.width, viewport.x, viewport.width)
      ? currentScrollPosition.x
      : clampedScrollPosition.x,
    y: isVisibleOnAxis(target.y, target.height, viewport.y, viewport.height)
      ? currentScrollPosition.y
      : clampedScrollPosition.y
  }
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

function clampViewportToScrollArea(viewport: Rect, scrollArea: Rect): Rect {
  return {
    ...viewport,
    x: clamp(
      viewport.x,
      scrollArea.x,
      Math.max(scrollArea.x + scrollArea.width - viewport.width, scrollArea.x)
    ),
    y: clamp(
      viewport.y,
      scrollArea.y,
      Math.max(scrollArea.y + scrollArea.height - viewport.height, scrollArea.y)
    )
  }
}

function isVisibleOnAxis(
  targetStart: number,
  targetSize: number,
  viewportStart: number,
  viewportSize: number
) {
  return (
    targetStart >= viewportStart &&
    targetStart + targetSize <= viewportStart + viewportSize
  )
}
